import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Grid,
    Paper,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import HardwareRoundedIcon from '@mui/icons-material/HardwareRounded'

import catalog from '../data/masterBlacksmithCatalog.json'
import CompactAssetIcon from './CompactAssetIcon'

const PLUGIN_KEY = 'grabtools'
const SETTINGS_KEY = 'masterBlacksmithSettings'
const EMPTY_CONFIG = Object.freeze({})
const catalogByID = new Map(catalog.items.map(item => [String(item.packageID), item]))

const cloneValue = value => typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value ?? {}))
const digits = (value, fallback = '') => String(value ?? '').replace(/[^\d]/g, '') || fallback
const positiveDigits = (value, fallback = '1') => {
    const parsed = Number.parseInt(digits(value), 10)
    return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : fallback
}
const formatNumber = value => Number(value || 0).toLocaleString('en-US')

export const normalizeMasterBlacksmithSettings = pluginConfig => {
    const source = pluginConfig?.[SETTINGS_KEY] && typeof pluginConfig[SETTINGS_KEY] === 'object' ? pluginConfig[SETTINGS_KEY] : {}
    const sourcePurchases = source.purchases && typeof source.purchases === 'object' ? source.purchases : {}
    return {
        intervalMinutes: positiveDigits(source.intervalMinutes, '15'),
        maxPurchasesPerRun: positiveDigits(source.maxPurchasesPerRun, '3'),
        allowRubyPurchases: source.allowRubyPurchases === true,
        coinReserve: digits(source.coinReserve, '1000'),
        purchases: Object.fromEntries(Object.entries(sourcePurchases).map(([packageID, purchase]) => [packageID, {
            enabled: purchase?.enabled === true,
            targetStock: digits(purchase?.targetStock ?? purchase?.amount, '0')
        }]))
    }
}

const formatRemaining = milliseconds => {
    if (!Number.isFinite(milliseconds))
        return 'Waiting for server time'
    if (milliseconds <= 0)
        return 'Ended'
    const totalSeconds = Math.ceil(milliseconds / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [days ? `${days}d` : '', `${hours}h`, `${minutes}m`, `${seconds}s`].filter(Boolean).join(' ')
}

const getIconType = item => ['soldier', 'unit'].includes(item.packageType) ? 'troop' :
    item.packageType === 'tool' ? 'tool' :
        ['deco', 'building', 'constructionItem'].includes(item.packageType) ? 'building' : 'resource'

function ItemIcon({ item, size = 60 }) {
    return (
        <Box sx={{ width: size, height: size, flexShrink: 0, position: 'relative', borderRadius: '10px', border: '1px solid rgba(211,180,126,.55)', background: 'linear-gradient(180deg,#ead7b9,#b89768)' }}>
            <CompactAssetIcon iconUrl={item.iconUrl} itemType={getIconType(item)} size={size} />
            {item.bagMaterialIconUrl ? (
                <CompactAssetIcon
                    iconUrl={item.bagMaterialIconUrl}
                    itemType="resource"
                    size={23}
                    sx={{ position: 'absolute', right: -3, bottom: -3, borderRadius: '50%', border: '2px solid #f0e3cb', backgroundColor: '#525c6c' }}
                />
            ) : null}
        </Box>
    )
}

function CurrencyIcon({ item, size = 25 }) {
    return item.currencyIconUrl ? <CompactAssetIcon iconUrl={item.currencyIconUrl} itemType="resource" size={size} /> : null
}

const PAGE_SIZE = 60

export default function MasterBlacksmithSettingsPanel({ userPlugins, onChange, runtimeStatus, botRunning = false, bthMode = false }) {
    const pluginConfig = React.useMemo(() => userPlugins?.[PLUGIN_KEY] ?? EMPTY_CONFIG, [userPlugins])
    const settings = React.useMemo(() => normalizeMasterBlacksmithSettings(pluginConfig), [pluginConfig])
    const [now, setNow] = React.useState(Date.now())
    const [query, setQuery] = React.useState('')
    const [currencyFilter, setCurrencyFilter] = React.useState('all')
    const [offerView, setOfferView] = React.useState('rotation')
    const [displayLimit, setDisplayLimit] = React.useState(PAGE_SIZE)

    React.useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(interval)
    }, [])

    const commit = React.useCallback(nextSettings => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = normalizeMasterBlacksmithSettings({ [SETTINGS_KEY]: nextSettings })
        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const updateSettings = React.useCallback(mutator => {
        const next = cloneValue(settings)
        mutator(next)
        commit(next)
    }, [commit, settings])

    const liveOffers = React.useMemo(() => (Array.isArray(runtimeStatus?.offers) ? runtimeStatus.offers : [])
        .map(offer => ({ ...(catalogByID.get(String(offer.packageID)) || {}), ...offer, isVerifiedRotation: true })), [runtimeStatus?.offers])
    const hasLiveOffers = liveOffers.length > 0
    const functionEnabled = pluginConfig?.state === true
    const selectedRotationOffers = React.useMemo(() => liveOffers
        .filter(item => settings.purchases[String(item.packageID)]?.enabled), [liveOffers, settings.purchases])
    const allDisplayedOffers = offerView === 'selected' ? selectedRotationOffers : liveOffers
    const currencies = React.useMemo(() => [...new Map(allDisplayedOffers.map(item => [item.currencyKey, {
        key: item.currencyKey,
        label: item.currencyLabel,
        iconUrl: item.currencyIconUrl
    }])).values()], [allDisplayedOffers])
    const normalizedQuery = query.trim().toLowerCase()
    const visibleOffers = allDisplayedOffers.filter(item =>
        (currencyFilter === 'all' || item.currencyKey === currencyFilter) &&
        (!normalizedQuery || String(item.label).toLowerCase().includes(normalizedQuery) || String(item.packageID).includes(normalizedQuery)))
    const selectedOffers = selectedRotationOffers
    const totals = selectedOffers.reduce((result, item) => {
        const target = Number(settings.purchases[String(item.packageID)]?.targetStock || 0)
        const current = Number(item.currentOwned)
        const count = Number.isFinite(current) ? Math.max(0, Math.ceil((target - current) / Math.max(1, Number(item.amount || 1)))) : 0
        result[item.currencyKey] = (result[item.currencyKey] || 0) + count * Number(item.price || 0)
        return result
    }, {})
    const eventEndsAt = Number(runtimeStatus?.eventEndsAt)
    const remainingText = formatRemaining(Number.isFinite(eventEndsAt) ? eventEndsAt - now : NaN)
    const statusUpdatedAt = Number(runtimeStatus?.updatedAt)
    const verifiedLabel = Number.isFinite(statusUpdatedAt) ? new Date(statusUpdatedAt).toLocaleString() : 'an earlier session'

    React.useEffect(() => {
        setDisplayLimit(PAGE_SIZE)
    }, [offerView, currencyFilter, normalizedQuery])

    const ensurePurchase = (next, key) => {
        next.purchases[key] ??= { enabled: false, targetStock: '0' }
        return next.purchases[key]
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '22px', bgcolor: 'rgba(18,29,48,.82)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Stack spacing={0.8}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                            <HardwareRoundedIcon sx={{ color: '#ffc857' }} />
                            <Typography variant="h5" fontWeight={900}>BTH Master Blacksmith</Typography>
                        </Stack>
                        <Typography color="text.secondary">Only the active game rotation, kept available as the last verified rotation while the bot is offline.</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip
                            icon={<AccessTimeRoundedIcon />}
                            color={runtimeStatus?.active ? 'success' : 'default'}
                            label={remainingText}
                        />
                        <Chip label={`${liveOffers.length} ${botRunning ? 'verified offers' : 'last verified offers'}`} />
                        <Chip color="primary" label={`${selectedOffers.length} selected`} />
                    </Stack>
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1.2 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label={bthMode ? 'Stock check interval (seconds)' : 'Refresh interval (minutes)'}
                            value={bthMode ? '30' : settings.intervalMinutes}
                            disabled={bthMode}
                            onChange={event => updateSettings(next => { next.intervalMinutes = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.intervalMinutes = positiveDigits(next.intervalMinutes, '15') })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Maximum packages per refresh"
                            value={settings.maxPurchasesPerRun}
                            onChange={event => updateSettings(next => { next.maxPurchasesPerRun = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.maxPurchasesPerRun = positiveDigits(next.maxPurchasesPerRun, '3') })}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
                            <Switch checked={settings.allowRubyPurchases} onChange={event => updateSettings(next => { next.allowRubyPurchases = event.target.checked })} />
                            <Box>
                                <Typography fontWeight={800}>Allow ruby purchases</Typography>
                                <Typography variant="caption" color="text.secondary">Locked by default</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    {bthMode && <Grid item xs={12} sm={6} md={4}>
                        <TextField fullWidth label="Coins to keep" value={settings.coinReserve}
                            onChange={event => updateSettings(next => { next.coinReserve = digits(event.target.value, '0') })}
                            helperText="Purchases must leave this reserve untouched." />
                    </Grid>}
                </Grid>
            </Paper>

            <Alert severity={botRunning && hasLiveOffers ? 'success' : functionEnabled ? 'info' : 'warning'}>
                {botRunning && hasLiveOffers ?
                    (runtimeStatus?.message || 'The current server rotation is loaded and verified.') :
                    !botRunning && hasLiveOffers ?
                        `Bot offline. Only the game rotation last verified ${verifiedLabel} is shown; stock and balances may have changed.` :
                        functionEnabled ?
                            'Start the bot to read the active Master Blacksmith rotation from the game.' :
                            'Enable Master Blacksmith in Function Selection and start the bot to read the active game rotation.'}
            </Alert>

            {Object.keys(totals).length > 0 ? (
                <Paper sx={{ p: 1.5, borderRadius: '16px', bgcolor: 'rgba(18,29,48,.72)' }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Typography fontWeight={800}>Selected cost:</Typography>
                        {Object.entries(totals).map(([currencyKey, total]) => {
                            const item = selectedOffers.find(offer => offer.currencyKey === currencyKey) || {}
                            return <Chip key={currencyKey} icon={<CurrencyIcon item={item} size={22} />} label={`${formatNumber(total)} ${item.currencyLabel || currencyKey}`} />
                        })}
                    </Stack>
                </Paper>
            ) : null}

            <Paper sx={{ p: 2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Stack spacing={1.5}>
                    <ToggleButtonGroup exclusive value={offerView} onChange={(_, value) => value && setOfferView(value)} sx={{ flexWrap: 'wrap' }}>
                        <ToggleButton value="rotation" sx={{ textTransform: 'none' }}>{botRunning ? 'Current rotation' : 'Last verified rotation'} ({liveOffers.length})</ToggleButton>
                        <ToggleButton value="selected" sx={{ textTransform: 'none' }}>Selected ({selectedOffers.length})</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField fullWidth label="Search offer or package ID" value={query} onChange={event => setQuery(event.target.value)} />
                    <ToggleButtonGroup exclusive value={currencyFilter} onChange={(_, value) => value && setCurrencyFilter(value)} sx={{ flexWrap: 'wrap' }}>
                        <ToggleButton value="all" sx={{ textTransform: 'none' }}>All currencies</ToggleButton>
                        {currencies.map(currency => (
                            <ToggleButton key={currency.key} value={currency.key} sx={{ textTransform: 'none', gap: 0.8 }}>
                                {currency.iconUrl ? <CompactAssetIcon iconUrl={currency.iconUrl} itemType="resource" size={24} /> : null}
                                {currency.label}
                                {runtimeStatus?.balances?.[currency.key] != null ? ` (${formatNumber(runtimeStatus.balances[currency.key])})` : ''}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            </Paper>

            {visibleOffers.length === 0 ? (
                <Alert severity="warning">{offerView === 'rotation' ? 'No active game rotation has been verified and saved yet. Start the bot to read it.' : 'No selected offer matches these filters.'}</Alert>
            ) : (
                <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                    {visibleOffers.slice(0, displayLimit).map(item => {
                        const key = String(item.packageID)
                        const purchase = settings.purchases[key] || { enabled: false, targetStock: '0' }
                        const blockedRuby = item.isRuby && !settings.allowRubyPurchases
                        return (
                            <Grid item xs={12} md={6} xl={4} key={key}>
                                <Paper sx={{ p: 1.6, height: '100%', borderRadius: '16px', bgcolor: item.inactive ? 'rgba(45,46,55,.55)' : 'rgba(20,31,52,.72)', opacity: item.inactive ? 0.72 : 1 }}>
                                    <Stack direction="row" spacing={1.1} alignItems="flex-start">
                                        <Checkbox
                                            checked={purchase.enabled}
                                            disabled={blockedRuby}
                                            onChange={event => updateSettings(next => { ensurePurchase(next, key).enabled = event.target.checked })}
                                        />
                                        <ItemIcon item={item} />
                                        <Stack spacing={0.45} flex={1} minWidth={0}>
                                            <Typography fontWeight={850} lineHeight={1.18}>{formatNumber(item.amount)}x {item.label}</Typography>
                                            <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <CurrencyIcon item={item} />
                                                <Typography fontWeight={900}>{formatNumber(item.price)}</Typography>
                                                <Typography variant="caption" color="text.secondary">{item.currencyLabel}</Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.stockRemaining == null ? 'Stock: server controlled' : `Remaining: ${formatNumber(item.stockRemaining)}`}
                                                {' · '}Package #{item.packageID}
                                            </Typography>
                                            <Typography variant="caption" color={item.inventoryMeasurable ? 'success.light' : 'warning.main'} fontWeight={800}>
                                                {item.inventoryMeasurable ? `Current stock: ${formatNumber(item.currentOwned)}` : 'Current stock unavailable - automatic purchase paused'}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                color={item.isVerifiedRotation ? 'success' : 'default'}
                                                label={botRunning ? 'Verified in current rotation' : 'Last verified rotation'}
                                                sx={{ alignSelf: 'flex-start' }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Maintain at least"
                                                value={purchase.targetStock}
                                                disabled={!purchase.enabled || blockedRuby}
                                                onChange={event => updateSettings(next => { ensurePurchase(next, key).targetStock = digits(event.target.value, '0') })}
                                                onBlur={() => updateSettings(next => { ensurePurchase(next, key).targetStock = digits(ensurePurchase(next, key).targetStock, '0') })}
                                                sx={{ mt: 0.4, maxWidth: 190 }}
                                            />
                                            {blockedRuby ? <Typography variant="caption" color="error.main">Enable ruby purchases above to select this offer.</Typography> : null}
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Grid>
                        )
                    })}
                </Grid>
                {visibleOffers.length > displayLimit ? (
                    <Button variant="outlined" onClick={() => setDisplayLimit(limit => limit + PAGE_SIZE)}>
                        Show next {Math.min(PAGE_SIZE, visibleOffers.length - displayLimit)} ({visibleOffers.length - displayLimit} remaining)
                    </Button>
                ) : null}
                </Stack>
            )}
        </Box>
    )
}
